import { Box, Divider, Link, List, ListItem, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownOptions, ReactNode } from 'react-markdown/lib/react-markdown';
import { useNavigate } from 'react-router';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

const Markdown = ({ children, components, rehypePlugins: rehypePluginsProp }: ReactMarkdownOptions) => {
    /** Merged plugins: duplicate `vfile` typings from nested deps break inference; runtime is fine. */
    const rehypePlugins = [...(rehypePluginsProp ?? []), rehypeRaw] as NonNullable<
        ReactMarkdownOptions['rehypePlugins']
    >;
    const navigate = useNavigate();
    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={rehypePlugins}
            components={{
                strong: ({ children }) => (
                    <Typography variant='body1' color='primary' component='span' fontWeight='bold'>
                        {children}
                    </Typography>
                ),
                a: ({ href, children }: { href?: string; children?: ReactNode }) => (
                    <Link
                        underline='always'
                        color='secondary'
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(href)}
                    >
                        {children}
                    </Link>
                ),
                ul: ({ children }) => (
                    <List
                        sx={{
                            listStyleType: 'disc',
                            paddingLeft: '2em',
                            py: 0,
                        }}
                    >
                        {children}
                    </List>
                ),
                ol: ({ children }) => (
                    <List
                        sx={{
                            listStyleType: 'decimal',
                            paddingLeft: '2em',
                            py: 0,
                        }}
                    >
                        {children}
                    </List>
                ),
                li: ({ children }) => (
                    <ListItem
                        sx={{
                            display: 'list-item',
                            py: 0,
                            paddingLeft: 0,
                            color: 'primary.main',
                        }}
                    >
                        {children}
                    </ListItem>
                ),
                h1: ({ children }) => (
                    <Typography variant='h5' color='primary'>
                        {children}
                    </Typography>
                ),
                h2: ({ children }) => (
                    <Typography variant='h6' color='primary'>
                        {children}
                    </Typography>
                ),
                h3: ({ children }) => (
                    <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                        {children}
                    </Typography>
                ),
                table: ({ children }) => <Table>{children}</Table>,
                tbody: ({ children }) => <TableBody>{children}</TableBody>,
                tr: ({ children }) => <TableRow>{children}</TableRow>,
                td: ({ children }) => (
                    <TableCell>
                        <Typography variant='body2' color='primary'>
                            {children}
                        </Typography>
                    </TableCell>
                ),
                th: ({ children }) => (
                    <TableCell>
                        <Typography variant='caption' fontWeight='bold' color='primary'>
                            {children}
                        </Typography>
                    </TableCell>
                ),
                p: ({ children }) => (
                    <Typography variant='body1' color='primary'>
                        {children}
                    </Typography>
                ),
                hr: () => <Divider />,
                br: () => <br />,
                img: ({ src, alt }) => (
                    <Box
                        component='img'
                        src={src}
                        alt={alt}
                        sx={{
                            maxWidth: '800px !important',
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            margin: '1em 0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    />
                ),
                video: ({ src, ...props }) => (
                    <Box
                        component='video'
                        src={src}
                        controls
                        playsInline
                        {...props}
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            display: 'block',
                            margin: '1em 0',
                            borderRadius: '4px',
                        }}
                    />
                ),
                ...components,
            }}
        >
            {children}
        </ReactMarkdown>
    );
};

export default Markdown;
